import React, { useState, useEffect } from "react";
import { ref, get, update } from "firebase/database";
import { database } from "../../firebase";
import TopBar from "../Sidebar/SidebarUser";
import ConfirmationDialog from './ConfirmationDialog';
import {
  Box,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Collapse,
  Alert,
  Stack,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Pagination,
  Modal,
  Fade,
  Backdrop,
  Paper
} from "@mui/material";
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  Event as EventIcon,
  Fingerprint as FingerprintIcon,
} from "@mui/icons-material";
import {
  StyledCard,
  StyledExpiredCard,
  LoadingContainer,
  LockerDetailsGrid,
  HistorySection,
  AnimationModal,
  ActionButton,
  StatusIcon
} from "./Userboard.styles";

const Userboard = () => {
  const [lockerInfo, setLockerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");
  const [isExpired, setIsExpired] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [overdueFee, setOverdueFee] = useState(0);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openAnimation, setOpenAnimation] = useState(false);
  const [animationStep, setAnimationStep] = useState("lock");
  const itemsPerPage = 5;

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("currentUser"));
    const currentUserEmail = storedUser ? storedUser.email : null;

    if (!currentUserEmail) {
      setLockerInfo(null);
      setLoading(false);
      return;
    }

    get(ref(database, "lockers"))
      .then((snapshot) => {
        setLoading(false);
        if (!snapshot.exists()) {
          setLockerInfo(null);
          return;
        }
        const lockersData = snapshot.val();
        const foundLocker = Object.entries(lockersData).find(
          ([, locker]) =>
            locker.assignedTo &&
            locker.assignedTo.toLowerCase() === currentUserEmail.toLowerCase()
        );
        setLockerInfo(foundLocker ? { id: foundLocker[0], ...foundLocker[1] } : null);
      })
      .catch((error) => {
        console.error("Error fetching lockers:", error);
        setError("Failed to fetch locker information. Please try again later.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!lockerInfo || !lockerInfo.expiryTime) {
      if (!loading) {
        setIsVerified(true);
      }
      return;
    }

    const checkExpiry = () => {
      const now = Date.now();
      const expiry = new Date(lockerInfo.expiryTime).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft("Expired");
        const calculatedOverdueFee = Math.ceil((now - expiry) / (1000 * 60) / 5);
        setOverdueFee(lockerInfo.overdueFee || calculatedOverdueFee);
      } else {
        setIsExpired(false);
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
        setOverdueFee(0);
      }
      setIsVerified(true);
    };

    checkExpiry();
    const intervalId = setInterval(checkExpiry, 1000);
    return () => clearInterval(intervalId);
  }, [lockerInfo, loading]);

  const handleToggleStatus = async () => {
    if (!lockerInfo || isExpired) return;
    if (lockerInfo.status === "locked") {
      setOpenConfirmDialog(true);
    } else {
      await performStatusToggle();
    }
  };

  const performStatusToggle = async () => {
    if (!lockerInfo) return;
    setUpdating(true);
    const newStatus = lockerInfo.status === "locked" ? "unlocked" : "locked";
    
    try {
      await update(ref(database, `lockers/${lockerInfo.id}`), { status: newStatus });
      setLockerInfo((prev) => ({ ...prev, status: newStatus }));
      if (newStatus === "unlocked") {
        triggerUnlockAnimation();
      }
    } catch (error) {
      setError("Failed to update locker status. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const triggerUnlockAnimation = () => {
    setAnimationStep("lock");
    setOpenAnimation(true);
    setTimeout(() => setAnimationStep("unlock"), 500);
    setTimeout(() => setOpenAnimation(false), 1500);
  };

  const handleConfirm = async () => {
    setOpenConfirmDialog(false);
    await performStatusToggle();
  };

  const getUserHistoryArray = () => {
    if (!lockerInfo?.userHistory) return [];
    if (Array.isArray(lockerInfo.userHistory)) return lockerInfo.userHistory;
    return Object.entries(lockerInfo.userHistory)
      .map(([key, value]) => ({ id: key, ...value }));
  };

  const renderLockerContent = () => {
    if (error) {
      return <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>;
    }

    if (!lockerInfo) {
      return (
        <Alert severity="info" icon={<LockIcon />} sx={{ mt: 4 }}>
          <Typography>No locker assigned to you at the moment.</Typography>
        </Alert>
      );
    }

    if (isExpired) {
      return (
        <StyledExpiredCard sx={{ borderRadius: 3, maxWidth: "90%", margin: "0 auto" }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningIcon color="error" />
              <Typography variant="h6" color="error">
                Your locker rental has expired.
              </Typography>
            </Stack>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              <strong>Overdue Fee:</strong> {overdueFee} Peso{overdueFee !== 1 ? "s" : ""}
            </Typography>
          </CardContent>
        </StyledExpiredCard>
      );
    }

    return (
      <StyledCard sx={{ borderRadius: 3, maxWidth: "90%", margin: "0 auto" }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <StatusIcon status={lockerInfo.status}>
              {lockerInfo.status === "locked" ? <LockIcon /> : <LockOpenIcon />}
            </StatusIcon>
            <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
              {lockerInfo.status}
            </Typography>
          </Stack>
          
          <LockerDetailsGrid>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Locker ID:</Typography>
                <Typography variant="body1">{lockerInfo.id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Assigned To:</Typography>
                <Typography variant="body1">{lockerInfo.assignedTo}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Availability:</Typography>
                <Typography variant="body1">{lockerInfo.availability}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Expiry Time:</Typography>
                <Typography variant="body1">
                  {new Date(lockerInfo.expiryTime).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Time Left:</Typography>
                <Typography variant="body1">{timeLeft}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Fingerprint ID:</Typography>
                <Typography variant="body1">{lockerInfo.fingerprintID}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment:</Typography>
                <Typography variant="body1">₱ {lockerInfo.sessionPayment}.00</Typography>
              </Grid>
            </Grid>
          </LockerDetailsGrid>

          <ActionButton>
            <Tooltip title={isExpired ? "Cannot modify status. Locker is expired." : ""} arrow>
              <span>
                <Button
                  variant="contained"
                  color={lockerInfo.status === "locked" ? "success" : "error"}
                  startIcon={lockerInfo.status === "locked" ? <LockOpenIcon /> : <LockIcon />}
                  onClick={handleToggleStatus}
                  disabled={updating || isExpired}
                  fullWidth
                >
                  {updating ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : lockerInfo.status === "locked" ? (
                    "Unlock Locker"
                  ) : (
                    "Lock Locker"
                  )}
                </Button>
              </span>
            </Tooltip>
          </ActionButton>

          <HistorySection>
            <Collapse in={showHistory}>
              <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                  Locker Rental History
                </Typography>
                {getUserHistoryArray().length > 0 ? (
                  <List>
                    {getUserHistoryArray()
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((item, idx) => (
                        <React.Fragment key={item.id || idx}>
                          <ListItem alignItems="flex-start">
                            <ListItemAvatar>
                              <Avatar>
                                {item.event === "rent" ? <EventIcon /> : <FingerprintIcon />}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                  {item.event.charAt(0).toUpperCase() + item.event.slice(1)}
                                </Typography>
                              }
                              secondary={
                                <>
                                  <Typography variant="body2" color="textSecondary">
                                    <strong>Timestamp:</strong>{" "}
                                    {new Date(item.timestamp).toLocaleString()}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    <strong>Fingerprint:</strong> {item.fingerprint}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    <strong>Added Minutes:</strong> {item.addedMinutes}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    <strong>Added Pesos:</strong> {item.addedPesos}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                          <Divider variant="inset" component="li" />
                        </React.Fragment>
                      ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No rental history available.
                  </Typography>
                )}
                {getUserHistoryArray().length > itemsPerPage && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={Math.ceil(getUserHistoryArray().length / itemsPerPage)}
                      page={currentPage}
                      onChange={(_, value) => setCurrentPage(value)}
                      color="primary"
                    />
                  </Box>
                )}
              </Box>
            </Collapse>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setShowHistory((prev) => !prev)}
              fullWidth
            >
              {showHistory ? "Hide History" : "Show History"}
            </Button>
          </HistorySection>
        </CardContent>
      </StyledCard>
    );
  };

  return (
    <Box sx={{ bgcolor: "#e6f2f4", minHeight: "100vh" }}>
      <TopBar />
      <Stack spacing={2} alignItems="center" sx={{ pt: 10, pb: 8 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#3b99a1",
            mb: 1,
            textAlign: "left",  // Aligns the Dashboard text to the left
          }}
        >
          Dashboard
        </Typography>
        {loading || !isVerified ? (
          <LoadingContainer>
            {loading ? (
              <CircularProgress />
            ) : (
              <Typography variant="body1" color="textSecondary">
                Verifying locker status...
              </Typography>
            )}
          </LoadingContainer>
        ) : (
          renderLockerContent()
        )}
      </Stack>

      <ConfirmationDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleConfirm}
      />

      <Modal
        open={openAnimation}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={openAnimation}>
          <AnimationModal>
            {animationStep === "lock" ? (
              <LockIcon sx={{ fontSize: 80, color: "error.main", transition: "all 0.5s ease" }} />
            ) : (
              <LockOpenIcon sx={{ fontSize: 80, color: "success.main", transition: "all 0.5s ease" }} />
            )}
            <Typography variant="h6" sx={{ mt: 2 }}>
              {animationStep === "lock" ? "Locking..." : "Unlocked!"}
            </Typography>
          </AnimationModal>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Userboard;
